type AppEnv = "development" | "staging" | "production";

interface AppConfig {
  readonly API_URL: string;
  readonly API_V2_URL: string;
  readonly MAIL_API_URL?: string;
  readonly APP_ENV: AppEnv;
  readonly isProduction: boolean;
  readonly isStaging: boolean;
  readonly isDevelopment: boolean;
  readonly isMailRequired: boolean;
  readonly showEnvIndicator: boolean;
  /** DSN de Sentry. Vacío si no está configurado. */
  readonly SENTRY_DSN: string;
  /** PostHog project token. Vacío si no está configurado. */
  readonly POSTHOG_TOKEN: string;
  /** PostHog API host. */
  readonly POSTHOG_HOST: string;
  /**
   * Client id de Google para el acceso de entrenadores. Vacío esconde el botón
   * y deja las pantallas funcionando solo con correo y contraseña. No es un
   * secreto: viaja en el bundle, y lo que lo protege son los orígenes
   * autorizados en Google Cloud.
   */
  readonly GOOGLE_CLIENT_ID: string;
}

const VALID_ENVS: AppEnv[] = ["development", "staging", "production"];
const rawEnv = import.meta.env.VITE_APP_ENV ?? "production";
const currentEnv: AppEnv = VALID_ENVS.includes(rawEnv as AppEnv)
  ? (rawEnv as AppEnv)
  : "production";

class ConfigManager {
  private static instance: AppConfig;

  private constructor() {}

  public static getInstance(): AppConfig {
    if (!ConfigManager.instance) {
      ConfigManager.validate();

      const isProd = currentEnv === "production";

      ConfigManager.instance = Object.freeze({
        API_URL: import.meta.env.VITE_API_URL,
        API_V2_URL: import.meta.env.VITE_API_V2_URL,
        MAIL_API_URL: import.meta.env.VITE_MAIL_API_URL,
        APP_ENV: currentEnv,
        isProduction: isProd,
        isStaging: currentEnv === "staging",
        isDevelopment: currentEnv === "development",
        isMailRequired: isProd,
        showEnvIndicator: !isProd,
        SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN ?? "",
        POSTHOG_TOKEN: import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ?? "",
        POSTHOG_HOST:
          import.meta.env.VITE_PUBLIC_POSTHOG_HOST ??
          "https://us.i.posthog.com",
        GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "",
      });
    }

    return ConfigManager.instance;
  }

  private static validate(): void {
    const errors: string[] = [];
    const isMailReq = currentEnv === "production";
    const apiUrl = import.meta.env.VITE_API_URL;
    const apiV2Url = import.meta.env.VITE_API_V2_URL;
    const mailUrl = import.meta.env.VITE_MAIL_API_URL;

    if (!apiUrl) errors.push("VITE_API_URL is required");
    if (!apiV2Url) errors.push("VITE_API_V2_URL is required");
    if (isMailReq && !mailUrl)
      errors.push("VITE_MAIL_API_URL is required in production");

    if (apiV2Url) {
      try {
        new URL(apiV2Url);
      } catch {
        errors.push("VITE_API_V2_URL must be a valid URL");
      }
    }

    [apiUrl, mailUrl].forEach((url, index) => {
      if (url) {
        try {
          new URL(url);
        } catch {
          errors.push(
            `${index === 0 ? "VITE_API_URL" : "VITE_MAIL_API_URL"} must be a valid URL`,
          );
        }
      }
    });

    if (errors.length > 0) {
      const message = `[Config Error]:\n${errors.map((e) => ` - ${e}`).join("\n")}`;
      console.error(message);
      throw new Error(message);
    }
  }
}

export const config = ConfigManager.getInstance();
